import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import PasswordInput from "../components/PasswordInput";
import { toast } from "sonner";
import api from "../lib/api";
import { RefreshCw } from "lucide-react";
import { suggestEmail } from "../lib/emailTypoSuggest";

const LS_REMEMBER_ID_KEY = "bpg.commerce.rememberedIdentifier";
const LS_REMEMBER_FLAG_KEY = "bpg.commerce.rememberMe";
const LS_LEGACY_EMAIL_KEY = "bpg.commerce.rememberedEmail";

export default function CommerceLogin() {
  const { loginCommerce } = useAuth();
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // Anti-spam: CAPTCHA only appears AFTER N failed attempts (server-driven).
  const [captchaNeeded, setCaptchaNeeded] = useState(false);
  const [captcha, setCaptcha] = useState({ id: "", question: "" });
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const loadCaptcha = async () => {
    try {
      const { data } = await api.get("/security/captcha");
      setCaptcha({ id: data.id, question: data.question });
      setCaptchaAnswer("");
    } catch {
      setCaptcha({ id: "", question: "Combien font 3 + 4 ?" });
    }
  };

  // Probe the server once on mount AND chaque fois que l'identifiant
  // change — pour les emails admin déclarés (présence de '@'), le serveur
  // retourne `required:false` immédiatement, donc le CAPTCHA est masqué
  // automatiquement même si l'IP a accumulé des échecs.
  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      try {
        const id = (form.identifier || "").trim();
        const params = id ? { params: { identifier: id } } : undefined;
        const { data } = await api.get("/auth/login/commerce/captcha-required", params);
        if (cancelled) return;
        if (data?.required) {
          setCaptchaNeeded(true);
          loadCaptcha();
        } else {
          setCaptchaNeeded(false);
          setCaptchaAnswer("");
        }
      } catch {}
    };
    probe();
    return () => { cancelled = true; };
  }, [form.identifier]);
  // Safe next URL — only same-origin relative paths are allowed (must start
  // with "/" and NOT with "//" to prevent open-redirects).
  const nextParam = searchParams.get("next") || "";
  const safeNext = (nextParam.startsWith("/") && !nextParam.startsWith("//")) ? nextParam : "";

  useEffect(() => {
    try {
      const saved = localStorage.getItem(LS_REMEMBER_ID_KEY)
        // Migration: previous versions saved the email; reuse it as identifier so
        // admins (whose identifier IS their email) keep their saved value.
        || localStorage.getItem(LS_LEGACY_EMAIL_KEY);
      const flag = localStorage.getItem(LS_REMEMBER_FLAG_KEY);
      if (saved) setForm((f) => ({ ...f, identifier: saved }));
      if (flag != null) setRememberMe(flag === "1");
    } catch {}
  }, []);

  // Spec utilisateur 2026-02 (fork 25.5) — l'identifiant de connexion est
  // désormais une adresse email. On laisse cependant le champ accepter du
  // texte libre pour préserver la connexion des anciens comptes basés sur
  // le nom (ex : TEST1). On NE force plus l'uppercase pour ne pas casser
  // la saisie d'un email.
  const handleIdentifierChange = (e) => {
    setForm({ ...form, identifier: e.target.value || "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const identifier = form.identifier.trim();
      const captchaPayload = captchaNeeded ? { id: captcha.id, answer: captchaAnswer } : null;
      const data = await loginCommerce(identifier, form.password, captchaPayload);
      try {
        if (rememberMe) {
          localStorage.setItem(LS_REMEMBER_ID_KEY, identifier);
          localStorage.setItem(LS_REMEMBER_FLAG_KEY, "1");
        } else {
          localStorage.removeItem(LS_REMEMBER_ID_KEY);
          localStorage.setItem(LS_REMEMBER_FLAG_KEY, "0");
        }
        // Always clean the legacy key once we have a fresh value.
        localStorage.removeItem(LS_LEGACY_EMAIL_KEY);
      } catch {}
      toast.success("Connecté !");
      // Admin → admin console; merchant → merchant dashboard (or safe next URL if provided).
      if (data?.role === "client" && data?.user?.isAdmin) {
        nav(safeNext || "/admin");
      } else {
        nav(safeNext || "/merchant/dashboard");
      }
    } catch (err) {
      const status = err.response?.status;
      const captchaHeader = err.response?.headers?.["x-captcha-required"];
      // After N failed attempts the backend gates the next request behind a
      // CAPTCHA — surface it inline. The 429 path means "blocked too long",
      // we just display the error.
      if (status === 403 && captchaHeader === "1") {
        setCaptchaNeeded(true);
        await loadCaptcha();
        toast.error("Vérification de sécurité requise — résolvez la question puis réessayez.");
      } else {
        toast.error(err.response?.data?.detail || "Erreur de connexion");
        // After a "wrong credentials" 401, refresh the captcha (single-use)
        // so subsequent attempts always have a valid token.
        if (captchaNeeded) await loadCaptcha();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 md:px-8 py-16 max-w-md">
      <Link to="/login" className="text-sm text-subtle hover:text-ink inline-flex items-center gap-1" data-testid="back-chooser-link">← Retour</Link>
      <p className="overline mb-2 mt-4">Établissement</p>
      <h1 className="font-display text-4xl font-black tracking-tight text-ink">Espace établissement</h1>
      <p className="mt-2 text-subtle">Connectez-vous à votre tableau de bord.</p>
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="commerce-login-form">
        <div>
          <Label htmlFor="clogin-identifier">Adresse email</Label>
          <Input
            id="clogin-identifier"
            name="identifier"
            type="text"
            inputMode="email"
            required
            autoComplete="username"
            autoCapitalize="off"
            spellCheck={false}
            placeholder="exemple@etablissement.fr"
            value={form.identifier}
            onChange={handleIdentifierChange}
            className="h-12 mt-1.5"
            data-testid="identifier-input"
          />
          {/* Spec fork 25.6 — suggestion de correction d'un domaine
             email manifestement mal orthographié (« gmial.com » →
             « gmail.com »). Calculée 100 % côté client. */}
          {(() => {
            const sug = suggestEmail(form.identifier);
            if (!sug) return null;
            return (
              <p
                className="mt-1.5 text-xs text-[#A65800] bg-[#FFF8EB] border border-[#F18F18]/40 rounded-md px-2 py-1.5 leading-snug"
                data-testid="email-typo-suggestion"
              >
                Vouliez-vous dire{" "}
                <button
                  type="button"
                  onClick={() => setForm({ ...form, identifier: sug })}
                  className="font-bold text-brand underline-offset-2 hover:underline"
                  data-testid="email-typo-suggestion-apply"
                >
                  {sug}
                </button>{" "}
                ?
              </p>
            );
          })()}
          <p className="mt-1.5 text-xs text-subtle">
            Saisissez l'adresse email associée à votre compte établissement.
          </p>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="clogin-password">Mot de passe</Label>
            <Link to="/forgot-password?role=commerce" className="text-xs text-brand hover:underline font-medium" data-testid="forgot-pwd-link">
              Mot de passe oublié ?
            </Link>
          </div>
          <PasswordInput
            id="clogin-password"
            name="password"
            required
            autoComplete="current-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="h-12 mt-1.5"
            data-testid="password-input"
          />
        </div>
        <div className="flex items-start gap-3 pt-1">
          <Checkbox
            id="remember-me-commerce"
            checked={rememberMe}
            onCheckedChange={(v) => setRememberMe(!!v)}
            className="mt-0.5"
            data-testid="remember-me-checkbox"
          />
          <label htmlFor="remember-me-commerce" className="text-sm text-ink leading-snug cursor-pointer">
            <span className="font-medium">Enregistrer mon adresse email</span>
            <span className="block text-xs text-subtle mt-0.5">
              Le mot de passe peut être enregistré par votre navigateur.
            </span>
          </label>
        </div>
        {/* === ANTI-SPAM CAPTCHA — affiché UNIQUEMENT en cas d'activité
           suspecte (N échecs récents depuis cette IP). Pas affiché sinon —
           expérience normale fluide pour les vrais utilisateurs. */}
        {captchaNeeded && (
          <div className="border border-[#F18F18] bg-[#FFF8EB] p-4 rounded-md" data-testid="login-captcha-block">
            <Label htmlFor="login-captcha-answer" className="text-sm font-bold text-[#A65800]">
              ⚠️ Vérification de sécurité
            </Label>
            <div className="mt-2 flex items-center gap-3">
              <p className="text-sm text-ink flex-1" data-testid="login-captcha-question">
                {captcha.question || "Chargement…"}
              </p>
              <button
                type="button"
                onClick={loadCaptcha}
                className="text-xs text-subtle hover:text-brand inline-flex items-center gap-1"
                aria-label="Régénérer la question"
              >
                <RefreshCw size={13} /> Régénérer
              </button>
            </div>
            <Input
              id="login-captcha-answer"
              type="text"
              inputMode="numeric"
              required={captchaNeeded}
              value={captchaAnswer}
              onChange={(e) => setCaptchaAnswer(e.target.value)}
              placeholder="Votre réponse"
              className="h-11 mt-2 max-w-[200px]"
              data-testid="login-captcha-input"
            />
            <p className="mt-2 text-[11px] text-subtle">
              Trop de tentatives ont été détectées depuis votre adresse. Cette étape protège votre compte.
            </p>
          </div>
        )}
        <Button type="submit" disabled={loading} className="w-full h-12 bg-ink hover:bg-brand rounded-md text-white font-medium" data-testid="submit-btn">
          {loading ? "…" : "Se connecter"}
        </Button>
      </form>
      <p className="mt-6 text-sm text-subtle">
        Nouvel établissement ? <Link to="/commerce/register" className="text-brand font-medium">Inscription gratuite</Link>
      </p>
    </div>
  );
}
